import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, take} from 'rxjs';
import {ActivatedRoute, Params, Router} from '@angular/router';
import { MotiUrlParams } from '../models/url';
import { ConvoId, TopicId } from '../models/uuid';

export const UrlPrefixConvos = '/convos';
export const UrlPrefixTeams = '/teams';
export const UrlPrefixTopics = '/topics';

export const URL_PARAM_KEY_SEARCH_TERM = 's';
export const URL_PARAM_KEY_PAGE_NUMBER = 'p';


@Injectable({
  providedIn: 'root',
})
export class MotiUrlService {
  private searchTerm: string;
  private pageNumber: number;

  readonly searchTerm$: Observable<string> = new Observable<string>(
    subscriber => {
      const sub = this._searchTerm$.subscribe({
        next: (searchTerm) => {
          subscriber.next(searchTerm);
        },
      });

      return () => {
        sub.unsubscribe();
      };
    },
  );
  readonly pageNumber$: Observable<number> = new Observable<number>(
    subscriber => {
      const sub = this._pageNumber$.subscribe({
        next: (pageNumber) => {
          subscriber.next(pageNumber);
        },
      });

      return () => {
        sub.unsubscribe();
      };
    },
  );

  private _searchTerm$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private _pageNumber$: BehaviorSubject<number> = new BehaviorSubject<number>(1);

  /**
   * Since contents of params are encoded JSON strings and strings are immutable,
   * storing the last params we got at any given time enables us to compare new
   * and previous params in a way that we could not after JSON.parse is called
   * since some of the params could be of mutable types.
   *
   * @private
   */
  private lastParams: Params = {};


  constructor(
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.route.queryParams.subscribe({
      next: (params) => {
        const searchTermChanged = params[URL_PARAM_KEY_SEARCH_TERM] !== this.lastParams[URL_PARAM_KEY_SEARCH_TERM];
        const pageNumberChanged = params[URL_PARAM_KEY_PAGE_NUMBER] !== this.lastParams[URL_PARAM_KEY_PAGE_NUMBER];

        // We can set this now that all the comparisons between old and new are complete
        this.lastParams = params;

        const urlParams: MotiUrlParams = {
          searchTerm: params[URL_PARAM_KEY_SEARCH_TERM] ? JSON.parse(params[URL_PARAM_KEY_SEARCH_TERM]) : undefined,
          pageNumber: params[URL_PARAM_KEY_PAGE_NUMBER] ? JSON.parse(params[URL_PARAM_KEY_PAGE_NUMBER]) : undefined,
        };

        this.searchTerm = urlParams.searchTerm;
        this.pageNumber = urlParams.pageNumber;

        if (searchTermChanged) {
          this._searchTerm$.next(this.searchTerm ?? '');
        }

        if (pageNumberChanged) {
          this._pageNumber$.next(this.pageNumber ?? 1);
        }
      },
    });
  }


  setPageNumber(p: number, replaceUrl = false) {
    if (p <= 1) {
      this.deleteQueryParam(URL_PARAM_KEY_PAGE_NUMBER);
    } else {
      this.upsertQueryParam(URL_PARAM_KEY_PAGE_NUMBER, p, replaceUrl);
    }
  }


  setSearchTerm(s: string, replaceUrl = false) {
    if (!s) {
      this.deleteQueryParam(URL_PARAM_KEY_SEARCH_TERM);
    } else {
      this.upsertQueryParam(URL_PARAM_KEY_SEARCH_TERM, s, replaceUrl);
    }
  }


  /**
   *
   * @param paramName
   * @param value
   * @param replaceUrl {boolean} Same as replaceUrl in Angular router
   *    interface called NavigationBehaviorOptions
   */
  private upsertQueryParam(paramName: string, value: any, replaceUrl: boolean = false) {
    const params: { [k: string]: string } = {};
    params[paramName] = JSON.stringify(value);

    this.router.navigate(
      [],
      {
        replaceUrl: replaceUrl,
        relativeTo: this.route,
        queryParams: params,
        queryParamsHandling: 'merge',
      },
    );
  }


  deleteQueryParam(paramName: string) {
    this.route.queryParams.pipe(
      take(1),
    ).subscribe({
      next: (params) => {
        if (params[paramName] !== undefined) {
          params = {...params};
          delete params[paramName];

          this.router.navigate(
            [],
            {
              relativeTo: this.route,
              queryParams: params,
              queryParamsHandling: '',
            },
          );
        }
      },
    });
  }


  static getConvoChatUrl(convoId: ConvoId): string {
    return `${UrlPrefixConvos}/${convoId}/chat`;
  }


  static getConvoUsersUrl(convoId: ConvoId): string {
    return `${UrlPrefixConvos}/${convoId}/users`;
  }


  static getConvoSettingsUrl(convoId: ConvoId): string {
    return `${UrlPrefixConvos}/${convoId}/settings`;
  }


  static getTopicUrl(topicId: TopicId): string {
    return `${UrlPrefixTopics}/${topicId}`;
  }


  static getTopicUsersUrl(topicId: TopicId): string {
    return `${UrlPrefixTopics}/${topicId}/users`;
  }
}
